import { useMemo, useState } from "react"
import { motion } from "motion/react"
import { Trophy01, ZapFast } from "@untitledui/icons"
import { ProtectedRoute } from "@/lib/auth/protected-route"
import { AppLayout } from "@/components/layout/app-layout"
import { ALL_ROLES, Roles } from "@/lib/team/roles"
import { useAuth } from "@/lib/auth/auth-context"
import { useOrganization } from "@/lib/organization/organization-context"
import { useEstimates } from "@/lib/api/use-estimates"
import { useTeam, useTeamMembers } from "@/lib/api/use-teams"
import { computeAdvisorStats } from "@/lib/dashboard/advisor-stats"
import type { TeamSettingsMetadata } from "@/lib/api/types"
import { cx } from "@/lib/utils/cx"

// Leaders — the leaderboard as a full phone screen with a sense of TIME:
// where the week stands (day meter), who moved TODAY (+N chips), what it
// takes to pass the next person, and a This-week / This-month switch.

type BoardEntry = {
  userId: number
  firstName: string
  lastName: string
  isYou: boolean
  rank: number
  count: number
  dollars: number | null
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"]

export default function LeadersPage() {
  const { user } = useAuth()
  const { activeTeamId } = useOrganization()
  const { data: team } = useTeam(activeTeamId)
  const { data: members } = useTeamMembers(activeTeamId ? Number(activeTeamId) : null)
  const { data: estimates = [] } = useEstimates(activeTeamId)
  const [period, setPeriod] = useState<"week" | "month">("week")

  const meta = (team?.metadata ?? {}) as TeamSettingsMetadata
  const firstName = user?.firstName?.trim() || "there"
  const meId = user?.id ? Number(user.id) : null
  const advisorCount = useMemo(
    () => (members ?? []).filter((m) => m.role === Roles.ServiceAdvisor).length,
    [members],
  )
  const stats = useMemo(
    () =>
      computeAdvisorStats({
        estimates,
        members,
        currentUserId: meId,
        currentUserFirstName: firstName,
        goalsRewards: meta.goalsRewards,
        baseline: meta.serviceLane,
        activeAdvisorCount: advisorCount,
      }),
    [estimates, members, meId, firstName, meta.goalsRewards, meta.serviceLane, advisorCount],
  )
  const memberMeta = useMemo(
    () =>
      new Map(
        (members ?? []).map((m) => [
          String(m.id),
          { role: m.role, department: m.department },
        ]),
      ),
    [members],
  )
  const storeWeeklyTarget = Number(meta.goalsRewards?.weeklyEstimateTarget ?? 0) || 0

  // Week board comes from the same engine as the dashboard card.
  const weekBoard: BoardEntry[] = useMemo(
    () =>
      (stats.leaderboardWeekly?.entries ?? []).map((e) => ({
        userId: e.userId,
        firstName: e.firstName,
        lastName: e.lastName,
        isYou: e.isYou,
        rank: e.rank,
        count: e.count,
        dollars: e.weekDollars,
      })),
    [stats.leaderboardWeekly],
  )

  // Month board — same population, month-to-date window, count-ranked.
  // (Month pay isn't a per-board number we track; the month view ranks work.)
  const monthBoard: BoardEntry[] = useMemo(() => {
    const start = new Date()
    start.setDate(1)
    start.setHours(0, 0, 0, 0)
    const byAuthor = new Map<number, BoardEntry>()
    for (const e of estimates) {
      if (e.isActive === false) continue
      if (e.status === "Draft" || e.status === "Loading") continue
      const c = e.createdBy
      if (c?.id == null) continue
      if (new Date(e.createdAt) < start) continue
      const cur = byAuthor.get(c.id) ?? {
        userId: c.id,
        firstName: c.firstName ?? "",
        lastName: c.lastName ?? "",
        isYou: meId != null && c.id === meId,
        rank: 0,
        count: 0,
        dollars: null,
      }
      cur.count++
      byAuthor.set(c.id, cur)
    }
    const list = [...byAuthor.values()].sort((a, b) => b.count - a.count)
    for (const e of list) e.rank = 1 + list.filter((x) => x.count > e.count).length
    return list
  }, [estimates, meId])

  // Who moved TODAY — momentum chips on the week view.
  const todayByAuthor = useMemo(() => {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    const m = new Map<number, number>()
    for (const e of estimates) {
      if (e.isActive === false) continue
      if (e.status === "Draft" || e.status === "Loading") continue
      const c = e.createdBy
      if (c?.id == null) continue
      if (new Date(e.createdAt) < start) continue
      m.set(c.id, (m.get(c.id) ?? 0) + 1)
    }
    return m
  }, [estimates])

  const board = period === "week" ? weekBoard : monthBoard
  const podium = board.slice(0, 3)
  const teamTotal = board.reduce((s, e) => s + e.count, 0)
  const teamPct =
    period === "week" && storeWeeklyTarget > 0
      ? Math.round((teamTotal / storeWeeklyTarget) * 100)
      : null

  // The pass line — what it takes to move up one seat (week view).
  const nudge = useMemo(() => {
    if (period !== "week") return null
    const me = board.find((e) => e.isYou)
    if (!me || me.rank <= 1) return null
    const above = board
      .filter((e) => e.count > me.count)
      .sort((a, b) => a.count - b.count)[0]
    if (!above) return null
    const gap = above.count - me.count
    if (gap > 5) return null
    return `${gap + 1} more ${gap + 1 === 1 ? "scan" : "scans"} to pass ${above.firstName} ${above.lastName.charAt(0)}. and take #${me.rank - 1}`
  }, [board, period])

  // Week meter: Mon–Fri pips, filled to today.
  const isoDay = (() => {
    const d = new Date().getDay()
    return d === 0 ? 7 : d
  })()

  return (
    <ProtectedRoute allowedRoles={ALL_ROLES}>
      <AppLayout>
        <div className="relative min-h-screen bg-gradient-to-b from-zinc-50 to-white pb-28">
          <div aria-hidden className="pointer-events-none fixed inset-0">
            <div className="absolute -top-32 right-[-200px] size-[680px] rounded-full bg-amber-100/40 blur-3xl" />
          </div>

          <div className="relative max-w-[560px] mx-auto px-4 md:px-6 pt-5 md:pt-8 space-y-4">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-700">
                  {period === "week" ? "This week" : "This month"}
                </p>
                <h1 className="text-[30px] leading-[1.08] font-black text-zinc-900 tracking-tight mt-0.5">
                  Leaderboard
                </h1>
              </div>
              <div className="size-11 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center shadow-lg shadow-amber-500/40 shrink-0">
                <Trophy01 className="size-5 text-white" aria-hidden />
              </div>
            </motion.div>

            {/* Time controls: the window switch + where the week stands. */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }} className="flex items-center justify-between gap-3">
              <div className="inline-flex items-center rounded-full bg-zinc-100 p-1">
                {(["week", "month"] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPeriod(p)}
                    className={cx(
                      "h-8 px-4 rounded-full text-[12.5px] font-bold transition-all",
                      period === p
                        ? "bg-white text-zinc-900 shadow-sm"
                        : "text-zinc-500 hover:text-zinc-800",
                    )}
                  >
                    {p === "week" ? "This week" : "This month"}
                  </button>
                ))}
              </div>
              {period === "week" && (
                <div className="flex items-center gap-1.5" aria-label="Week progress">
                  {WEEKDAYS.map((d, i) => {
                    const dayNum = i + 1
                    const isPast = dayNum < isoDay
                    const isToday = dayNum === isoDay
                    return (
                      <div key={d} className="flex flex-col items-center gap-1">
                        <span
                          className={cx(
                            "h-1.5 w-6 rounded-full",
                            isPast && "bg-amber-400",
                            isToday && "bg-amber-500 animate-pulse",
                            !isPast && !isToday && "bg-zinc-200",
                          )}
                        />
                        <span
                          className={cx(
                            "text-[8.5px] font-bold uppercase tracking-wide",
                            isToday ? "text-amber-700" : "text-zinc-400",
                          )}
                        >
                          {d[0]}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </motion.div>

            {board.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-2xl border border-zinc-200/80 bg-white p-10 text-center"
              >
                <p className="text-[14px] font-semibold text-zinc-900">
                  {period === "week" ? "The week just started" : "The month just started"}
                </p>
                <p className="text-[13px] text-zinc-500 mt-1">
                  First estimate takes #1 — that could be you in the next ten minutes.
                </p>
              </motion.div>
            ) : (
              <>
                {/* The stage — big podium, center seat tallest. */}
                {podium.length >= 3 && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }} className="flex items-end justify-center gap-3 pt-2">
                    {[podium[1], podium[0], podium[2]].map((e, i) => {
                      const place = i === 1 ? 1 : i === 0 ? 2 : 3
                      return (
                        <div
                          key={e.userId}
                          className="flex-1 max-w-[150px] flex flex-col items-center min-w-0"
                        >
                          <div
                            className={cx(
                              "rounded-2xl grid place-items-center font-black shadow-lg mb-1.5",
                              place === 1
                                ? "size-14 text-[16px] bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-amber-500/40"
                                : place === 2
                                  ? "size-12 text-[14px] bg-zinc-200 text-zinc-600 shadow-zinc-300/50"
                                  : "size-12 text-[14px] bg-orange-100 text-orange-800 shadow-orange-200/50",
                            )}
                          >
                            {e.firstName.charAt(0)}
                            {e.lastName.charAt(0)}
                          </div>
                          <p
                            className={cx(
                              "text-[13.5px] font-bold truncate max-w-full",
                              e.isYou ? "text-emerald-700" : "text-zinc-900",
                            )}
                          >
                            {e.isYou ? "You" : `${e.firstName} ${e.lastName.charAt(0)}.`}
                          </p>
                          <p className="text-[11px] text-zinc-400 tabular-nums mb-2">
                            {e.count} {e.count === 1 ? "car" : "cars"}
                          </p>
                          <div
                            className={cx(
                              "w-full rounded-t-2xl grid place-items-start justify-center pt-2",
                              place === 1
                                ? "h-24 bg-gradient-to-b from-amber-300/80 to-amber-200/40"
                                : place === 2
                                  ? "h-16 bg-zinc-100"
                                  : "h-12 bg-orange-100/70",
                            )}
                          >
                            <span
                              className={cx(
                                "text-[20px] font-black",
                                place === 1
                                  ? "text-amber-800"
                                  : place === 2
                                    ? "text-zinc-500"
                                    : "text-orange-800/80",
                              )}
                            >
                              {place}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </motion.div>
                )}

                {/* Full standings — everyone, ranked, with identity + today's
                    momentum. */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className={cx(
                    "rounded-2xl border border-zinc-200/70 bg-white shadow-sm overflow-hidden relative",
                    podium.length >= 3 && "-mt-4",
                  )}
                >
                  <div className="px-3 py-2 space-y-1">
                    {board.map((e) => {
                      const m = memberMeta.get(String(e.userId))
                      const today = period === "week" ? (todayByAuthor.get(e.userId) ?? 0) : 0
                      return (
                        <div
                          key={e.userId}
                          className={cx(
                            "flex items-center gap-3 px-2.5 py-2 rounded-xl",
                            e.isYou && "bg-emerald-50/70 ring-1 ring-inset ring-emerald-300/40",
                          )}
                        >
                          <span
                            className={cx(
                              "size-7 rounded-full grid place-items-center text-[12px] font-black shrink-0",
                              e.rank === 1
                                ? "bg-gradient-to-br from-amber-300 to-amber-500 text-white shadow-md shadow-amber-500/40"
                                : e.isYou
                                  ? "bg-gradient-to-br from-emerald-400 to-emerald-600 text-white"
                                  : "bg-zinc-100 text-zinc-500",
                            )}
                          >
                            {e.rank}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <p
                                className={cx(
                                  "text-[14px] font-bold truncate leading-tight",
                                  e.isYou ? "text-emerald-900" : "text-zinc-900",
                                )}
                              >
                                {e.isYou ? "You" : `${e.firstName} ${e.lastName.charAt(0)}.`}
                              </p>
                              {today > 0 && (
                                <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-bold tabular-nums">
                                  +{today} today
                                </span>
                              )}
                            </div>
                            {m && (
                              <p className="text-[10.5px] text-zinc-400 truncate leading-tight">
                                {m.role}
                                {m.department ? ` · ${m.department}` : ""}
                              </p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-[15px] font-black text-zinc-900 tabular-nums leading-none">
                              {e.count}
                            </p>
                            <p className="text-[10px] text-zinc-400 tabular-nums">
                              {e.dollars != null ? `$${e.dollars}` : e.count === 1 ? "car" : "cars"}
                            </p>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {nudge && (
                    <div className="px-4 py-2.5 border-t border-emerald-100 bg-emerald-50/60">
                      <p className="text-[11.5px] font-semibold text-emerald-800 leading-snug">
                        {nudge}
                      </p>
                    </div>
                  )}

                  {teamPct !== null && (
                    <div className="px-4 py-3 border-t border-amber-100 bg-amber-50/50 flex items-center gap-3">
                      <ZapFast className="size-5 text-amber-600 shrink-0" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="text-[12.5px] font-bold text-amber-900 leading-tight">
                          {teamPct >= 100
                            ? "Store goal made"
                            : teamPct >= 85
                              ? "Closing in on the store goal"
                              : "Team total"}
                          <span className="font-semibold text-amber-800/80">
                            {" "}
                            — {teamPct}% of the weekly store goal
                          </span>
                        </p>
                        <div className="mt-1.5 h-2 rounded-full bg-amber-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-amber-500"
                            style={{ width: `${Math.min(100, teamPct)}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-[20px] font-black text-amber-900 tabular-nums shrink-0">
                        {teamTotal}
                      </span>
                    </div>
                  )}
                </motion.div>
              </>
            )}
          </div>
        </div>
      </AppLayout>
    </ProtectedRoute>
  )
}
